import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Clock, Plus, RotateCcw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useComplaintList } from "@/hooks/useComplaints";
import { ComplaintRow } from "@/components/complaint/ComplaintRow";
import { CreateComplaintDrawer } from "@/components/complaint/CreateComplaintDrawer";
import { ComplaintDetailDrawer } from "@/components/complaint/ComplaintDetailDrawer";
import { SCOPE_LABEL, VIEW_DEFS, type ComplaintViewDef } from "@/components/complaint/constants";
import type { ComplaintGroupCounts, ComplaintListView, ComplaintScope } from "@/api/types";

export function ComplaintsListPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  // 🔴 D-149 N0-L (§1.13) — work-surface `complaint_queue` мусить бути АДРЕСОВНОЮ:
  // черга ручного призначення існувала як scope «Без призначення», але лише як
  // локальний стан, тобто на неї не було URL і поверхню не можна було відкрити
  // посиланням. Тепер `/complaints?scope=manual_queue` — заморожений route+filter
  // для отримувачів complaint_manual_assignment_required.
  const scopeParam = searchParams.get("scope") as ComplaintScope | null;
  const initialScope: ComplaintScope =
    scopeParam && ["my", "team", "all", "manual_queue", "sq_queue"].includes(scopeParam)
      ? scopeParam
      : "my";
  // COMP-LIFECYCLE layout v1.1 — default scope «Мої» + group «Усі» (full picture of own
  // complaints across all stages; nothing hidden behind a default filter).
  const [view, setView] = useState<ComplaintListView>("all");
  const [scope, setScope] = useState<ComplaintScope>(initialScope);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  // COMP-S3 W1 — after-create reassurance: where the draft went + how to reopen.
  const [draftToast, setDraftToast] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const listQ = useComplaintList({ view, scope, search });
  const data = listQ.data;
  const allowedScopes = data?.allowedScopes ?? ["my", "team"];

  // Глибоке посилання не має ставати мовчазним обходом ACL: якщо бекенд не дав
  // цей scope, повертаємось на «Мої» (сервер лишається джерелом істини).
  useEffect(() => {
    if (data && !allowedScopes.includes(scope)) {
      setScope("my");
    }
  }, [data, allowedScopes, scope]);

  // v1.1 — no auto-switch to my_action (group-first; «Потребує дії» is an opt-in flag, not
  // a default filter). The default stays scope «Мої» + group «Усі».

  return (
    <>
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[960px] space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Рекламації</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Реєструй, веди та вирішуй звернення клієнтів.
              </p>
            </div>
            <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" /> Нова рекламація
            </Button>
          </div>

          {/* Вісь «Обсяг / черга» (M-1) — scope tabs (Служба якості/Без призначення = черги). */}
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Обсяг / черга
            </div>
            <div className="inline-flex rounded-md border p-0.5">
              {allowedScopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={cn(
                    "rounded px-3 py-1 text-xs font-medium transition-colors",
                    scope === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {SCOPE_LABEL[s] ?? s}
                </button>
              ))}
            </div>
          </div>

          {/* Вісь «Стадія» — 6 lifecycle group cards + «Усі» (головний статус-рівень; backend counts). */}
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Стадія
            </div>
            <div className="flex flex-wrap gap-2">
              {VIEW_DEFS.map((v) => {
                const count =
                  v.groupKey && data
                    ? data.groupCounts[v.groupKey]
                    : v.k === "all" && data
                      ? totalCount(data.groupCounts)
                      : undefined;
                return (
                  <GroupCard key={v.k} def={v} count={count} active={view === v.k} onClick={() => setView(v.k)} />
                );
              })}
            </div>
          </div>

          {/* Флаги — тонка смуга «Потребує уваги» (cross-stage; backend-scoped counters; клік=фільтр). */}
          {data && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg bg-muted/40 px-3.5 py-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Потребує уваги
              </span>
              <FlagChip
                icon={AlertCircle}
                label="Потребує дії"
                count={data.counters.myAction}
                tone="amber"
                active={view === "my_action"}
                onClick={() => setView(view === "my_action" ? "all" : "my_action")}
              />
              <FlagChip
                icon={Clock}
                label="Прострочено дедлайн"
                count={data.counters.breached}
                tone="red"
                active={view === "breached"}
                onClick={() => setView(view === "breached" ? "all" : "breached")}
              />
              <span className="text-[11px] text-muted-foreground/70">
                Фільтри-індикатори поперек стадій — не окремі статуси
              </span>
            </div>
          )}

          <div className="flex max-w-md items-center gap-2 rounded-md border bg-background px-3 py-1.5 focus-within:ring-2 focus-within:ring-ring">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Шукати за номером або темою…"
              className="h-7 border-0 px-0 focus-visible:ring-0"
            />
          </div>

          {listQ.isPending ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[72px]" />
              ))}
            </div>
          ) : listQ.isError ? (
            <div className="rounded-lg border bg-destructive/5 p-6 text-center">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
              <p className="text-sm text-destructive">
                {(listQ.error as Error)?.message ?? "Не вдалося завантажити"}
              </p>
              <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => void listQ.refetch()}>
                <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
              </Button>
            </div>
          ) : data && data.items.length === 0 ? (
            <div className="rounded-lg border bg-muted/30 p-8 text-center">
              {view === "drafts" ? (
                <>
                  <p className="text-sm font-medium">У вас немає чернеток рекламацій</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Чернетки з’являються, якщо ви зберегли рекламацію без подання — щоб дозаповнити товар, партію та докази й подати пізніше.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">Немає рекламацій у цій категорії</p>
                  <p className="mt-1 text-xs text-muted-foreground">Зареєструйте рекламацію клієнта, щоб почати.</p>
                </>
              )}
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />Нова рекламація
              </button>
              {view !== "all" && (
                <div>
                  <button type="button" onClick={() => setView("all")} className="mt-2 text-xs text-primary hover:underline">
                    Показати всі
                  </button>
                </div>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {data?.items.map((c) => (
                <li key={c.id}>
                  <ComplaintRow complaint={c} onOpen={() => navigate(`/complaints/${c.id}`)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {draftToast && (
        <div className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4">
          <div className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3 text-sm shadow-xl">
            <span>Чернетку збережено. Дозаповніть товар, партію та докази й подайте пізніше.</span>
            <button
              type="button"
              onClick={() => {
                const id = draftToast;
                setDraftToast(null);
                navigate(`/complaints/${id}`);
              }}
              className="shrink-0 rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Відкрити чернетку
            </button>
            <button
              type="button"
              onClick={() => setDraftToast(null)}
              aria-label="Закрити"
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <CreateComplaintDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(newId) => {
          // W1/W2 — don't silently bounce into the draft (and don't let the list's
          // my_action auto-switch hide it). Confirm + offer to open it explicitly.
          setCreateOpen(false);
          setDraftToast(newId);
        }}
      />

      <ComplaintDetailDrawer
        complaintId={id ?? null}
        open={!!id}
        onClose={() => navigate("/complaints")}
      />
    </>
  );
}

function totalCount(gc: ComplaintGroupCounts): number {
  return gc.drafts + gc.new + gc.in_work + gc.waiting + gc.decided + gc.done;
}

// Level-1 lifecycle group card (the «Стадія» axis — the main status level).
function GroupCard({
  def,
  count,
  active,
  onClick,
}: {
  def: ComplaintViewDef;
  count: number | undefined;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-[112px] flex-1 rounded-xl border px-3.5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "border-primary bg-primary/5" : "border-border bg-background hover:border-primary/50",
      )}
    >
      <div className="text-[13px] font-semibold leading-tight">{def.label}</div>
      <div className={cn("mt-0.5 text-2xl font-bold leading-none", count === 0 && "text-muted-foreground/70")}>
        {count ?? "—"}
      </div>
      <div className="mt-1 text-[10.5px] text-muted-foreground">{def.sub}</div>
    </button>
  );
}

// Cross-stage attention flag (thin «Потребує уваги» bar — NOT a competing stage). Backend
// counters are scope+ACL-aware; click toggles the technical filter (deep-link compatible).
function FlagChip({
  icon: Icon,
  label,
  count,
  tone,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  tone: "amber" | "red";
  active: boolean;
  onClick: () => void;
}) {
  const toneText = tone === "amber" ? "text-warning-foreground" : "text-destructive";
  const toneActive = tone === "amber" ? "bg-warning" : "bg-destructive/10";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        toneText,
        active ? toneActive : "hover:bg-background",
        count === 0 && "opacity-50",
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
      <span className="font-bold">{count}</span>
    </button>
  );
}
